import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Users, MapPin, Languages, Check, X, ArrowLeft, MessageCircle, Wine } from "lucide-react";
import { useLanguage, pick, waLink } from "@/i18n";
import { api } from "@/api";
import BookingWidget from "@/components/BookingWidget";
import ExperienceCard from "@/components/ExperienceCard";
import FavoriteButton from "@/components/FavoriteButton";
import { useSEO } from "@/hooks/useSEO";
import { track } from "@/utils/track";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const [exp, setExp] = useState(null);
  const [addons, setAddons] = useState([]);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setExp(null);
    setError(false);
    api.experience(slug).then(setExp).catch(() => setError(true));
    api.addons().then(setAddons).catch(() => {});
    api.experiences().then((all) => {
      const current = all.find((e) => e.slug === slug);
      if (!current) return;
      const rel = all
        .filter((e) => e.slug !== slug)
        .sort((a, b) => {
          const score = (e) =>
            (e.destination === current.destination ? 2 : 0) +
            (e.categories.some((c) => current.categories.includes(c)) ? 1 : 0);
          return score(b) - score(a);
        })
        .slice(0, 3);
      setRelated(rel);
    }).catch(() => {});
  }, [slug]);

  useSEO(
    exp ? `${pick(exp.title, lang)} — Flegrea Experience` : "Flegrea Experience",
    exp ? pick(exp.short, lang) : "",
    exp
      ? {
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          name: pick(exp.title, lang),
          description: pick(exp.short, lang),
          image: exp.image,
          provider: { "@type": "Organization", name: "Flegrea Experience" },
        }
      : null
  );

  if (error) {
    return (
      <div className="py-40 text-center" data-testid="experience-not-found">
        <p className="font-serif text-3xl text-ink">{t("not_found")}</p>
        <Link to="/experiences" className="mt-6 inline-block text-sm text-medblue underline">{t("back_experiences")}</Link>
      </div>
    );
  }

  if (!exp) {
    return <div className="py-40 text-center text-ink/40" data-testid="loading-indicator">{t("loading")}</div>;
  }

  const title = pick(exp.title, lang);
  const matchedAddons = addons.filter((a) => (exp.addons || []).includes(a.slug));
  const faqs = [
    { q: t("faq_q1"), a: t("faq_a1") },
    { q: t("faq_q2"), a: t("faq_a2") },
    { q: t("faq_q3"), a: t("faq_a3") },
  ];

  return (
    <div data-testid="experience-detail-page">
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden">
        <img src={exp.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-16 w-full">
          <Link to="/experiences" data-testid="back-to-experiences" className="inline-flex items-center gap-2 text-ivory/70 text-xs uppercase tracking-[0.2em] mb-6 hover:text-ivory transition-colors duration-300">
            <ArrowLeft size={14} strokeWidth={1.5} /> {t("back_experiences")}
          </Link>
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            {exp.badges.map((b) => (
              <span key={b} className="bg-ivory/90 text-ink text-[10px] font-medium uppercase tracking-[0.15em] px-3 py-1.5">
                {t(`badge_${b.replace(/ /g, "_")}`)}
              </span>
            ))}
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-ivory tracking-tight leading-[1.05] max-w-3xl" data-testid="experience-title">
            {title}
          </h1>
          <p className="mt-4 text-ivory/80 max-w-xl text-base md:text-lg" data-testid="experience-short">{pick(exp.short, lang)}</p>
        </div>
      </section>

      {/* META BAR */}
      <section className="border-b border-stone2/50 bg-sand" data-testid="experience-meta-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Clock, label: t("duration"), value: pick(exp.duration, lang), id: "meta-duration" },
            { icon: Users, label: t("group_size"), value: pick(exp.group_size, lang), id: "meta-group" },
            { icon: MapPin, label: t("location_label"), value: pick(exp.meeting_point, lang), id: "meta-location" },
            { icon: Languages, label: t("languages_label"), value: exp.languages.join(" / "), id: "meta-languages" },
          ].map((m) => (
            <div key={m.id} data-testid={m.id}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink/40 mb-2">
                <m.icon size={13} strokeWidth={1.5} /> {m.label}
              </div>
              <p className="text-sm font-medium text-ink">{m.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTO GALLERY */}
      {Array.isArray(exp.gallery) && exp.gallery.length > 0 && (
        <section className="border-b border-stone2/40 py-14 overflow-hidden" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-8 flex items-end justify-between gap-6">
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-medblue">{t("gallery_title")}</p>
            <p className="hidden md:block text-[10px] text-ink/40 uppercase tracking-[0.2em]">{t("gallery_hint")}</p>
          </div>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-6 lg:px-10 pb-2" data-testid="gallery-strip">
            {exp.gallery.map((g, i) => (
              <motion.div
                key={i}
                data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                className="shrink-0 w-72 md:w-96 snap-start overflow-hidden"
              >
                <img src={g} alt={`${title} — ${i + 1}`} loading="lazy" className="w-full h-56 md:h-72 object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-20">
          {/* THE EXPERIENCE */}
          <section data-testid="the-experience-section">
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-6">{t("the_experience")}</h2>
            <p className="text-ink/70 leading-relaxed text-base md:text-lg">{pick(exp.description, lang)}</p>
          </section>

          {/* ANECDOTE / LOCAL MYTH */}
          {exp.anecdote && (
            <motion.section
              data-testid="anecdote-section"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-sand px-8 py-12 md:px-14 md:py-16 overflow-hidden"
            >
              <span aria-hidden="true" className="pointer-events-none absolute -top-6 left-4 font-serif text-[10rem] leading-none text-terracotta/15 select-none">
                &ldquo;
              </span>
              <p className="relative text-[11px] font-medium uppercase tracking-[0.3em] text-medblue mb-4" data-testid="anecdote-overline">
                {t("anecdote_overline")}
              </p>
              <h3 className="relative font-serif text-2xl md:text-3xl text-ink tracking-tight mb-4 max-w-xl" data-testid="anecdote-title">
                {pick(exp.anecdote.title, lang)}
              </h3>
              <p className="relative font-serif italic text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl" data-testid="anecdote-text">
                {pick(exp.anecdote.text, lang)}
              </p>
            </motion.section>
          )}

          {/* NARRATIVE CHAPTERS */}
          {Array.isArray(exp.chapters) && exp.chapters.length > 0 && (
            <section data-testid="chapters-section">
              <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-3xl md:text-4xl text-ink mb-12"
              >
                {t("chapters_title")}
              </motion.h2>
              <div className="space-y-20">
                {exp.chapters.map((c, i) => (
                  <motion.article
                    key={i}
                    data-testid={`chapter-${i}`}
                    initial={{ opacity: 0, y: 56 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
                    className="relative border-t border-stone2/60 pt-10"
                  >
                    <span aria-hidden="true" className="pointer-events-none absolute -top-8 right-0 font-serif text-[7rem] md:text-[9rem] leading-none text-terracotta/15 select-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-medblue mb-3" data-testid={`chapter-kicker-${i}`}>
                      {pick(c.kicker, lang)}
                    </p>
                    <h3 className="font-serif text-3xl md:text-4xl text-ink tracking-tight mb-6 max-w-xl" data-testid={`chapter-title-${i}`}>
                      {pick(c.title, lang)}
                    </h3>
                    <div className={c.image ? "grid md:grid-cols-2 gap-10 items-center" : ""}>
                      <p className="text-ink/70 leading-relaxed text-base md:text-lg max-w-2xl">{pick(c.text, lang)}</p>
                      {c.image && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.96 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, margin: "-60px" }}
                          transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <motion.img
                            src={c.image}
                            alt={pick(c.title, lang)}
                            loading="lazy"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            className="w-full h-64 md:h-80 object-cover"
                          />
                        </motion.div>
                      )}
                    </div>
                  </motion.article>
                ))}
              </div>
            </section>
          )}

          {/* HIGHLIGHTS */}
          <section data-testid="highlights-section">
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-8">{t("highlights")}</h2>
            <ul className="grid sm:grid-cols-2 gap-4">
              {exp.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 border border-stone2/50 p-5" data-testid={`highlight-${i}`}>
                  <Check size={16} strokeWidth={1.5} className="text-sage mt-0.5 shrink-0" />
                  <span className="text-sm text-ink/80">{pick(h, lang)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* INCLUDED / NOT INCLUDED */}
          <section className="grid sm:grid-cols-2 gap-10" data-testid="included-section">
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-sage mb-5">{t("included")}</h3>
              <ul className="space-y-3">
                {exp.included.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={15} strokeWidth={1.5} className="text-sage mt-0.5 shrink-0" /> {pick(item, lang)}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-terracotta mb-5">{t("not_included")}</h3>
              <ul className="space-y-3">
                {exp.excluded.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-ink/80">
                    <X size={15} strokeWidth={1.5} className="text-terracotta mt-0.5 shrink-0" /> {pick(item, lang)}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ADD-ONS */}
          {matchedAddons.length > 0 && (
            <section className="bg-sand p-8 lg:p-12" data-testid="addons-section">
              <div className="flex items-center gap-3 mb-3">
                <Wine size={18} strokeWidth={1.25} className="text-terracotta" />
                <h2 className="font-serif text-3xl text-ink">{t("addons_title")}</h2>
              </div>
              <p className="text-sm text-ink/60 mb-8">{t("addons_sub")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {matchedAddons.map((a) => (
                  <div key={a.slug} className="bg-ivory p-5 flex flex-col gap-2 border border-stone2/40" data-testid={`addon-${a.slug}`}>
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-medium text-ink">{pick(a.name, lang)}</h4>
                      <span className="text-sm text-medblue whitespace-nowrap">
                        {t("on_request")}
                      </span>
                    </div>
                    <p className="text-xs text-ink/50 leading-relaxed">{pick(a.description, lang)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* MEET YOUR GUIDE */}
          <section className="border-t border-stone2/50 pt-12" data-testid="meet-guide-section">
            <h2 className="font-serif text-3xl text-ink mb-4">{t("meet_guide")}</h2>
            <p className="text-ink/60 leading-relaxed">{t("meet_guide_text")}</p>
            <p className="mt-4 font-serif italic text-xl text-medblue">Knowledge makes the difference.</p>
          </section>

          {/* GOOD TO KNOW */}
          <section data-testid="good-to-know-section">
            <h2 className="font-serif text-3xl text-ink mb-6">{t("good_to_know")}</h2>
            <ul className="space-y-3">
              {[t("gtk_1"), t("gtk_2"), t("gtk_3")].map((g, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-ink/70">
                  <span className="w-1 h-1 rounded-full bg-terracotta mt-2 shrink-0" /> {g}
                </li>
              ))}
            </ul>
          </section>

          {/* WHAT TO BRING */}
          <section data-testid="what-to-bring-section">
            <h2 className="font-serif text-3xl text-ink mb-6">{t("what_to_bring")}</h2>
            <ul className="grid sm:grid-cols-3 gap-4">
              {[t("wtb_1"), t("wtb_2"), t("wtb_3")].map((w, i) => (
                <li key={i} className="border border-stone2/50 p-5 text-sm text-ink/70">{w}</li>
              ))}
            </ul>
          </section>

          {/* FAQ */}
          <section data-testid="faq-section">
            <h2 className="font-serif text-3xl text-ink mb-6">{t("faq_title")}</h2>
            <Accordion type="single" collapsible>
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-ink/60 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* BOOKING SIDEBAR */}
        <aside className="lg:col-span-1">
          <BookingWidget exp={exp} addons={matchedAddons} />
        </aside>
      </div>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-sand py-20" data-testid="related-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-12">{t("related_title")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
              {related.map((r) => (
                <ExperienceCard key={r.slug} exp={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STICKY MOBILE CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ivory/95 backdrop-blur border-t border-stone2/60 px-4 py-3 flex items-center justify-end gap-3" data-testid="sticky-mobile-cta">
        <div className="flex gap-2">
          <FavoriteButton slug={exp.slug} className="border border-stone2" />
          <a href="#booking-widget" data-testid="sticky-book-button"
            onClick={() => track("book_now_click", { slug: exp.slug })}
            className="bg-medblue text-ivory text-[11px] font-medium uppercase tracking-[0.15em] px-5 py-3 hover:bg-ink transition-colors duration-300">
            {t("book_now")}
          </a>
        </div>
      </div>
    </div>
  );
}
